import type { BaseRuntimeConfig } from '@blufin/web';
import { clsx } from 'clsx';
import React, { StrictMode, useCallback } from 'react';
import styles from './SiteContainerAlpha.module.scss';
import { NextJsEnvironmentProviderAlpha } from '../../../../context/EnvironmentContext/NextJsEnvironmentProviderAlpha/NextJsEnvironmentProviderAlpha';
import { HttpClientPublicAlpha } from '../../../../context/HttpContext/HttpClientPublicAlpha/HttpClientPublicAlpha';
import { SiteHttpProviderAlpha } from '../../../../context/HttpContext/HttpClientPublicAlpha/SiteHttpProviderAlpha';
import { useAppSelectability } from '../../../../hooks/useAppSelectability/useAppSelectability';
import { useEnvironmentAlpha } from '../../../../hooks/useEnvironment/useEnvironmentAlpha';
import { useHttpClientBeta } from '../../../../hooks/useHttpClient/useHttpClientBeta';
import { useSleepDetectionAlpha } from '../../../../hooks/useSleepDetection/useSleepDetectionAlpha';
import type { BaseReactComponentProps } from '../../../../types/app.types';
import { DevFooterPortalHost } from '../../_shared/DevFooterPortalHost/DevFooterPortalHost';

/** Loaded on demand: the footer never mounts in `prod`, so production bundles must not carry it. */
const SiteContainerDevFooterAlpha = React.lazy(() =>
  import('./partials/SiteContainerDevFooterAlpha').then(m => ({ default: m.SiteContainerDevFooterAlpha }))
);

export interface SiteContainerAlphaProps extends BaseReactComponentProps {
  runtimeConfig: BaseRuntimeConfig;
  version?: string;
}

const SiteContainerAlphaInner: React.FC<SiteContainerAlphaProps> = (props: SiteContainerAlphaProps) => {
  const { children } = props;

  const { appIsUnselectable, setAppIsUnselectable } = useAppSelectability();

  const environmentClient = useEnvironmentAlpha();
  const httpClient = useHttpClientBeta();

  /** Sleep detection to clear HMAC cache when computer wakes up */
  useSleepDetectionAlpha({
    onWakeFromSleep: useCallback(() => {
      if (httpClient instanceof HttpClientPublicAlpha) httpClient.clearHmacCache();
    }, [httpClient]),
    sleepThresholdMs: 30000
  });

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      className={clsx(styles.siteContainerAlpha, appIsUnselectable && styles.unselectable)}
      role="application"
      onMouseUp={() => setAppIsUnselectable(false)}>
      {children}
      {environmentClient.environment !== 'prod' ? (
        <React.Suspense fallback={null}>
          <DevFooterPortalHost>
            <SiteContainerDevFooterAlpha />
          </DevFooterPortalHost>
        </React.Suspense>
      ) : null}
    </div>
  );
};

/**
 * Container component for Next.js public sites.
 * Provides environment config, HTTP client (with HMAC), and sleep detection — without auth, app-wide stores,
 * or workers.
 * @param props - Component props including runtimeConfig and optional version.
 */
export const SiteContainerAlpha: React.FC<SiteContainerAlphaProps> = (props: SiteContainerAlphaProps) => {
  const { runtimeConfig, version } = props;

  return (
    <NextJsEnvironmentProviderAlpha runtimeConfig={runtimeConfig} version={version}>
      <SiteHttpProviderAlpha>
        <StrictMode>
          <SiteContainerAlphaInner {...props} />
        </StrictMode>
      </SiteHttpProviderAlpha>
    </NextJsEnvironmentProviderAlpha>
  );
};
